import { esc } from '../esc';
import { methodsForCurrency } from '../methods';
import type { DepositMethod } from '../types';
import { INPUT_CLS, LABEL_CLS } from './classes';

export interface CurrencySelectorOptions {
  /** Game currencies (GET /payments/methods → currencies). */
  currencies: string[];
  value: string;
  /** When given, currencies no enabled method can charge are hidden. */
  methods?: DepositMethod[];
  label?: string;
  onChange?: (currency: string) => void;
}

export interface CurrencySelectorHandle {
  destroy(): void;
  getValue(): string;
  setValue(currency: string): void;
}

/** Currencies that at least one enabled method can charge. */
export function chargeableCurrencies(currencies: readonly string[], methods?: readonly DepositMethod[]): string[] {
  if (!methods) return [...currencies];
  const enabled = methods.filter((m) => m.enabled !== false);
  return currencies.filter((c) => methodsForCurrency(enabled, c).length > 0);
}

/** A native <select> of charge currencies (keyboard + mobile friendly). */
export function mountCurrencySelector(el: HTMLElement, opts: CurrencySelectorOptions): CurrencySelectorHandle {
  const options = chargeableCurrencies(opts.currencies, opts.methods);
  let value = options.includes(opts.value) ? opts.value : (options[0] ?? opts.value);

  el.innerHTML = `
    <label class="block">
      <span class="${LABEL_CLS} mb-1.5">${esc(opts.label ?? 'Currency')}</span>
      <select name="currency" class="${INPUT_CLS}">
        ${options.map((c) => `<option value="${esc(c)}"${c === value ? ' selected' : ''}>${esc(c)}</option>`).join('')}
      </select>
    </label>`;
  const select = el.querySelector('select') as HTMLSelectElement;
  const onChange = () => { value = select.value; opts.onChange?.(value); };
  select.addEventListener('change', onChange);

  return {
    destroy() { select.removeEventListener('change', onChange); el.innerHTML = ''; },
    getValue: () => value,
    setValue(c) {
      if (!options.includes(c)) return;
      value = c;
      select.value = c;
    },
  };
}
